import type * as React from 'react'

import { EmptyState } from '@buffer-mono/popcorn'

import { ApprovalsEmptyStateIllustration } from './ApprovalsEmptyStateIllustration'
import { DraftsEmptyStateIllustration } from './DraftsEmptyStateIllustration'
import type { PostTab } from './PostTabs'
import { QueueEmptyStateIllustration } from './QueueEmptyStateIllustration'
import { SentEmptyStateIllustration } from './SentEmptyStateIllustration'

import styles from './PostEmptyStateByTab.module.css'

import type { JSX } from 'react'

export const PostEmptyStateByTab = ({
  tab,
  children,
  hasChannels = true,
  canManageTeamMembers = false,
  isOwner = false,
}: {
  tab: PostTab
  children?: React.ReactNode
  hasChannels?: boolean
  canManageTeamMembers?: boolean
  isOwner?: boolean
}): JSX.Element => {
  const emptyStateValues: Record<
    PostTab,
    {
      icon: React.ReactNode
      variant: 'success' | 'primary'
      heading: string
      description: string
    }
  > = {
    approvals: {
      icon: <ApprovalsEmptyStateIllustration />,
      variant: 'success',
      heading: 'Good job. You are all done!',
      description: 'This is where drafts awaiting approval will appear',
    },
    queue: {
      icon: <QueueEmptyStateIllustration />,
      variant: 'primary',
      heading: 'No posts scheduled',
      description: 'Schedule some posts and they will appear here',
    },
    sent: {
      icon: <SentEmptyStateIllustration />,
      variant: 'primary',
      heading: 'No sent posts',
      description:
        'You have not sent any posts yet, once you do they will appear here',
    },
    drafts: {
      icon: <DraftsEmptyStateIllustration />,
      variant: 'primary',
      heading: 'No drafts',
      description:
        'You have not saved any drafts yet, once you do they will appear here',
    },
  }

  const emptyStateTextWhenNoChannels = isOwner
    ? 'Connect a channel to start posting and managing your comments'
    : canManageTeamMembers
      ? "As an admin of this team, you can grant yourself access to your team's social channels, or connect your own channels. For questions, please contact your team admin."
      : "Once you have been granted access to your team's social channels, you'll see them here! For questions, please contact your team admin."

  const description = !hasChannels
    ? emptyStateTextWhenNoChannels
    : emptyStateValues[tab].description

  const heading = !hasChannels
    ? 'Welcome to Buffer 👋'
    : emptyStateValues[tab].heading

  return (
    <EmptyState
      size="large"
      variant={emptyStateValues[tab].variant}
      className={styles.root}
    >
      <div className={styles.illustration}>{emptyStateValues[tab].icon}</div>
      <EmptyState.Heading>{heading}</EmptyState.Heading>
      <EmptyState.Description>{description}</EmptyState.Description>
      <EmptyState.Actions className={styles.actions}>
        {children}
      </EmptyState.Actions>
    </EmptyState>
  )
}
